import React, { FormEvent, use } from "react";
import { useTranslation } from "react-i18next";
import ActionAvatars from "pages/conductLegalResearch/fragments/actionAvatars";
import { formatDay, formatDate, getUserType, calculateRemainingDays as calcRemainingDays } from "utils/helpers";
import UploadProposal from "./uploadProposal";
import AssignCoordinatorModal from "./assignCordinatorModal";
import { StatusChip } from "components/StatusChip";
import { LoadingIcon } from "utils/constant";
import SettingUpTeamModal from "./settingupTeamModal";
import SchedulePresentationModal from "./schedulePresentationModal";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import {
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_TYPES,
} from "utils/constantValues";
import {
  createRespondBackFeedback,
  getInternalLegalResearchRequest,
  getLegalResearchRequest,
  getLegalResearchTopics,
  updateInternalLegalResearchRequest,
  updateLegalResearchTopic,
} from "services/legalResearchService";
import { ChangeEvent, useEffect, useState } from "react";
import { getResearchSchedules } from "services/researchScheduleService";
import FileView from "components/fileView";
import ManagementFeedback from "./managementFeedback";
import ScheduleTitlePresentationModal from "./scheduleTitlePresentationModal";
import { Leaf } from "lucide-react";
import RespondBackForm from "./respondBackform";
import CreateInternalNewRequest from "../createInternalNewRequest";
import TeamView from "components/team_view";
import TopicList from "./TopicList";
import { prosecutors } from "utils/data";
import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import BroadcastCard from "./broadcastCard";

type UrgencyLevel = "High" | "Medium" | "Low";
interface Schedule {
  id: string;
  location: string;
  purpose: string;
  remark: string;
  research_request: string;
  scheduled_datetime: string;
}

interface UrgencyConfig {
  label: string;
  color: string;
  bgColor: string;
  maxDays: number;
}

const URGENCY_CONFIG: Record<string, UrgencyConfig> = {
  high: {
    label: "High Priority",
    color: "#DC2626",
    bgColor: "#FEE2E2",
    maxDays: 3
  },
  medium: {
    label: "Medium Priority",
    color: "#D97706",
    bgColor: "#FEF3C7",
    maxDays: 7
  },
  low: {
    label: "Low Priority",
    color: "#059669",
    bgColor: "#D1FAE5",
    maxDays: 14
  }
};

function InternalProposalDocument({ filteredData }: { filteredData: any }) {
  const { t } = useTranslation();
  type UrgencyLevel = "High" | "Medium" | "Low";
  const navigate = useNavigate();
  const { showToast } = useToast();
  const contextData = useAuth();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isScheduleTitleModalOpen, setIsScheduleTitleModalOpen] = useState(false);
  const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false);
  const [isFeedbackFormOpen, setIsFeedBackFormOpen] = useState(false);
  const [isEditProposalModalOpen, setIsEditProposalModalOpen] = useState(false);
  const [isProposalSummaryEditing, setIsProposalSummaryEditing] = useState(false);
  const [editedProposalSummary, setEditedProposalSummary] = useState("");
  const [editedProposalFile, setEditedProposalFile] = useState<any>(null);
  const [showSynopsisHistory, setShowSynopsisHistory] = useState(false);
  const [viewTeam, setViewTeam] = useState(false);

  const [schedules, setSchedules] = useState<Schedule | null>(null);
  const [topic, setTopic] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [remark, setRemark] = useState<string>("");
  const [respondBackStatus, setRespondBackStatus] = useState<string>(
    LEGAL_RESEARCH_REQUEST_STATUS.proposal_revision.value
  );
  const [respondBackForward, setRespondBackForward] = useState<string>(
    LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value
  );

  const [formState, setFormState] = useState<any>({ ...filteredData });
  useEffect(() => {
    setFormState({ ...filteredData });
  }, [filteredData]);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const topicdata = await getLegalResearchTopics(contextData.token);
        console.log("Fetched topics:", topicdata);

        if (!Array.isArray(topicdata)) {
          console.error("Expected an array but got:", topicdata);
          showToast(
            t("UnexpectedResponseFormat"),
            "error"
          );
          return;
        }

        const filtered = topicdata.find(
          (d: any) => d.internal_research_request === formState.id
        );
        setTopic(filtered);
      } catch (error) {
        console.error(
          "Error fetching internal legal research topic request:",
          error
        );
        showToast(
          t("FailedToFetchInternalLegalResearchTopicRequest"),
          "error"
        );
      }
    };

    if (contextData?.token && formState.id) {
      fetchTopics();
    }
  }, [contextData?.token, formState.id]);

  useEffect(() => {
    getResearchSchedules(contextData.token)
      .then((data) => {
        const filtered = data.filter(
          (schedule: any) =>
            schedule.research_request === topic.research_request ||
            schedule.internal_research_request ===
              topic.internal_research_request
        );

        const proposalSchedule = filtered?.find(
          (schedule: any) => schedule.purpose === "proposal_presentation"
        );

        setSchedules(proposalSchedule || filtered[0] || null);

        console.log("selected schedule: ", proposalSchedule || filtered[0]);
      })
      .catch((error) => {
        console.error("Error fetching legal research request:", error);
        showToast(t("FailedToFetchLegalResearchRequest"), "error");
      });
  }, [topic]);

  // Calculate urgency and remaining days
  const getUrgencyConfig = () => {
    const urgencyLevel = formState?.urgency_level?.toLowerCase() || 'medium';
    return URGENCY_CONFIG[urgencyLevel] || URGENCY_CONFIG.medium;
  };

  const calculateRemainingDays = () => {
    if (!schedules?.scheduled_datetime) return 0;
    const scheduledDate = new Date(schedules.scheduled_datetime);
    const now = new Date();
    const diffTime = scheduledDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const remainingDays = calculateRemainingDays();
  const urgencyConfig = getUrgencyConfig();
  const isUrgent = remainingDays <= 2;

  const handleRemarkChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setRemark(event.target.value);
  };
  
  const handleAcceptTopics = async () => {
    try {
      const topics = formState.internal_rsearc_topics || [];

      for (const topic of topics) {
        const formData = new FormData();
        console.log(topic);
        formData.append("topic_title", topic.topic_title);
        formData.append("is_accepted", String(topic.is_accepted));
        formData.append("is_approved", String(topic.is_approved));
        formData.append(
          "priority",
          topic.priority ? String(topic.priority) : ""
        );
        formData.append("research_request", topic.research_request);

        if (topic.internal_research_request)
          formData.append(
            "internal_research_request",
            topic.internal_research_request
          );

        await updateLegalResearchTopic(contextData.token, topic.id, formData);
      }

      const data = new FormData();
      if (
        formState.status ===
        LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value
      )
        data.append(
          "status",
          LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value
        );
      else
        data.append(
          "status",
          LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value
        );
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        data
      );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        urgency_level: response.urgency_level
          ? response.urgency_level.charAt(0).toUpperCase() +
            response.urgency_level.slice(1).toLowerCase()
          : "High",
      }));
      showToast(t("TopicsApproved"), "success");
      setEdit(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateTitleApproval"), "error");
    }
  };
  
  const handleUpdateProposalSummary = async () => {
    setLoading(true);
    const data = new FormData();
    data.append("proposal_summary", editedProposalSummary);
    data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value);
    if (editedProposalFile) data.append("proposal_file", editedProposalFile);

    try {
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        data
      );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        proposal_summary: editedProposalSummary,
      }));
      showToast(t("ProposalSummaryUpdatedSuccess"), "success");
      setIsProposalSummaryEditing(false);
      setEditedProposalFile(null);
    } catch (error) {
      console.error("Error updating proposal summary:", error);
      showToast(t("FailedToUpdateProposalSummary"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTitle = async () => {
    setLoading(true);
    const data = new FormData();

    data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value);
    data.append("title_remark", remark);

    try {
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        data
      );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        urgency_level: response.urgency_level
          ? response.urgency_level.charAt(0).toUpperCase() +
            response.urgency_level.slice(1).toLowerCase()
          : "High",
      }));
      showToast(t("TitleApprovedSuccess"), "success");
      setEdit(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateTitleApproval"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleForward = async (froward_status: string, status?: string) => {
    const data = new FormData();
    data.append("forward_status", froward_status);
    if (status) data.append("status", status);

    try {
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        data
      );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        urgency_level: response.urgency_level
          ? response.urgency_level.charAt(0).toUpperCase() +
            response.urgency_level.slice(1).toLowerCase()
          : "High",
      }));
      showToast(t("LegalResearchForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const data = new FormData();

    data.append("remark_for_director", remark);
    data.append("urgency_level", formState.urgency_level.toLowerCase());

    try {
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        data
      );

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        urgency_level: response.urgency_level
          ? response.urgency_level.charAt(0).toUpperCase() +
            response.urgency_level.slice(1).toLowerCase()
          : "High",
      }));
      showToast(t("LegalResearchRespondedBackSuccess"), "success");
      setEdit(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRespondBack = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);
    const data = new FormData();

    data.append("research_request", formState.id);
    data.append("given_by", contextData?.user?.full_name || "");
    data.append("role", getUserType(contextData?.user?.roles[0]));
    data.append("feedback_type", "stakeholder_request");
    data.append("note", remark);
    data.append("status", "respond_back");

    try {
      const feedbackResponse = await createRespondBackFeedback(
        contextData.token,
        data
      );
      const researchData = new FormData();
      researchData.append(
        "status",
        LEGAL_RESEARCH_REQUEST_STATUS.responded.value
      );
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        researchData
      );
      setFormState((prev: any) => ({ ...prev, ...response }));
      setEdit(false);
      showToast(t("LegalResearchRespondedBackSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProposalRespondBack = async () => {
    setLoading(true);
    const data = new FormData();

    data.append(
      "status",
      LEGAL_RESEARCH_REQUEST_STATUS.proposal_revision.value
    );
    data.append(
      "forward_status",
      LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value
    );
    data.append("feedback_from_management", remark);

    try {
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        data
      );

      setFormState((prev: any) => ({ ...prev, response }));
      showToast(t("ProposalFeedbackSubmittedSuccess"), "success");
      setIsRespondBackFormOpen(false);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateProposalFeedback"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProposalReject = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);
    const data = new FormData();

    data.append(
      "status",
      LEGAL_RESEARCH_REQUEST_STATUS.proposal_rejected.value
    );
    data.append("feedback_from_managment", remark);

    try {
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        data
      );

      setFormState((prev: any) => ({ ...prev, response }));
      showToast(t("ProposalRejectedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateProposalFeedback"), "error");
    } finally {
      setLoading(false);
      setIsRespondBackFormOpen(!isRespondBackFormOpen);
    }
  };

  const handleProposalRespondBackClick = () => {
    setIsRespondBackFormOpen(!isRespondBackFormOpen);
  };

  const handleProposalApprove = async () => {
    setLoading(true);
    const data = new FormData();

    data.append(
      "status",
      LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value
    );

    const isDirector = contextData?.user?.roles?.some(
      (p) => p === "LRDIFUDDirector"
    );
    const isCoordinator = contextData?.user?.roles?.some(
      (p) => p === "LegalResearchCoordinator"
    );

    if (formState?.forward_status === "forwarded_to_director") {
      if (isDirector) {
        data.append(
          "forward_status",
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value
        );
      }
    } else if (isDirector) {
      data.append(
        "forward_status",
        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value
      );
    } else if (isCoordinator) {
      data.append(
        "forward_status",
        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value
      );
    } else {
      data.append(
        "forward_status",
        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value
      );
    }

    try {
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        data
      );

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("ProposalApprovedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateProposalApproval"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUrgencyClick = (level: UrgencyLevel) => {
    if (formState.status != LEGAL_RESEARCH_REQUEST_STATUS.pending.value) return;
    setFormState((prev: any) => ({
      ...prev,
      urgency_level: level,
    }));
  };

  const getUrgencyButtonClasses = (level: UrgencyLevel) => {
    const baseClasses =
      "px-4 py-2 rounded-md font-semibold transition-colors duration-200";
    if (formState.urgency_level === level) {
      switch (level) {
        case "High":
          return `${baseClasses} bg-red-500 text-white`;
        case "Medium":
          return `${baseClasses} bg-yellow-500 text-white`;
        case "Low":
          return `${baseClasses} bg-green-500 text-white`;
        default:
          return "";
      }
    } else {
      return `${baseClasses} bg-gray-200 text-gray-700 hover:bg-gray-300`;
    }
  };

  const handleUploadProposalClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSchduleCloseModal = () => {
    setIsScheduleModalOpen(false);
  };

  const handleSchduleTitleCloseModal = () => {
    setIsScheduleTitleModalOpen(false);
  };

  const handleCloseTeamModal = () => {
    setIsTeamModalOpen(false);
  };

  const handleAssignProsecutorClick = () => {
    setIsTeamModalOpen(true);
  };

  const handleAssignCoordinatorClick = () => {
    setIsAssignModalOpen(true);
  };

  const handleAssignCloseModal = () => {
    setIsAssignModalOpen(false);
  };

  const handleBroadcastToPublic = async () => {
    setLoading(true);
    const data = new FormData();
    const newPublishedState = !formState.is_published;
    data.append("is_published", newPublishedState ? "true" : "false");

    try {
      const response = await updateInternalLegalResearchRequest(
        contextData.token,
        formState.id,
        data
      );

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(
        newPublishedState
          ? t("ResearchBroadcastedToPublic")
          : t("ResearchUnpublished"),
        "success"
      );
    } catch (error) {
      console.error("Error updating publication status:", error);
      showToast(t("FailedToUpdateBroadcastStatus"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log(formState);
  }, [formState]);
  
  return (
    <>
      <div className="flex h-full grow flex-col max-w-4xl bg-white rounded-lg shadow-md p-6 overflow-y-scroll scrollbar-hide">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-6">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-gray-800">
            {t("ConductResearchRequest")}
          </h1>
          <div className="flex gap-4">
            <div className="my-auto">
              <StatusChip
                status={
                  Object.values(LEGAL_RESEARCH_REQUEST_STATUS)?.find(
                    (s) => s.value === formState?.status
                  )?.label ||
                  formState?.status ||
                  ""
                }
              />
            </div>
            {formState.remark_for_director &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.pending.value && (
                <div className="flex gap-4">
                  <button
                    onClick={() => setEdit(!edit)}
                    type="button"
                    className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                  >
                    {edit ? t("Cancel") : (
                      <svg
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                          fill="black"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            {formState.remark_for_coordinator &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.pending.value &&
              contextData?.user?.roles?.some(
                (p) => p === "LRDIFUDDirector"
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.remark_for_team &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.pending.value &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator"
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.proposal_summary &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
              (formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.proposal_requested.value ||
                formState.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value) &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchProsecutor"
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.internal_topic_schdule?.find(
              (schedule: any) => schedule.purpose === "proposal_presentation"
            ) &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value &&
              contextData?.user?.roles?.some((p) => p === "GeneralAttorney") && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS
                .proposal_presentation_feedback_submitted.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator"
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value,
                        LEGAL_RESEARCH_REQUEST_STATUS.proposal_update.value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.received.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.not_forwarded.value &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator"
              ) && (
                <div className="flex gap-4">
                  {formState.requested_by === "internal" && (
                    <div className="flex gap-4">
                      <button
                        onClick={() =>
                          navigate(`/conduct-legal-research/internalRequest/`, {
                            state: { formState: formState },
                          })
                        }
                        type="button"
                        className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                      >
                        {!edit ? (
                          <svg
                            width="25"
                            height="24"
                            viewBox="0 0 25 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                              fill="black"
                            />
                          </svg>
                        ) : t("Cancel")}
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchProsecutor"
              ) && (
                <div className="flex gap-4">
                  {formState.requested_by === "internal" && (
                    <div className="flex gap-4">
                      <button
                        onClick={() =>
                          navigate(
                            `/conduct-legal-research/editInternalrequest/${formState.id}`,
                            { state: { formState: formState } }
                          )
                        }
                        type="button"
                        className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                      >
                        {!edit ? (
                          <svg
                            width="25"
                            height="24"
                            viewBox="0 0 25 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                              fill="black"
                            />
                          </svg>
                        ) : t("Cancel")}
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.forward_status ===
              LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
              !formState.internal_topic_schdule?.find(
                (schedule: any) => schedule.purpose === "title_presentation"
              ) &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator"
              ) && (
                <div className="flex gap-4">
                  <div className="flex gap-4">
                    <button
                      onClick={() => setEdit(!edit)}
                      type="button"
                      className="ml-auto hover:bg-gray-200 text-gray-500 bg-white rounded-lg my-auto px-2"
                    >
                      {!edit ? (
                        <svg
                          width="25"
                          height="24"
                          viewBox="0 0 25 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                            fill="black"
                          />
                        </svg>
                      ) : t("Cancel")}
                    </button>
                  </div>
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.internal_topic_schdule?.find(
              (schedule: any) => schedule.purpose === "title_presentation"
            ) &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some((p) => p === "GeneralAttorney") && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management
                          .value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
              formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some(
                (p) => p === "OAGBManagementHead"
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                          .value
                      )
                    }
                    type="button"
                    className="bg-[#212121] text-white rounded-lg px-4"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
            {formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value &&
              !formState.proposal_summary &&
              (formState.forward_status ===
                LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value ||
                formState.forward_status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value) &&
              formState.requested_by === "internal" &&
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator"
              ) && (
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleForward(
                        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                          .value
                      )
                    }
                    type="button"
                    className="ml-auto bg-[#212121] text-white rounded-lg px-4 py-2"
                  >
                    {t("Forward")}
                  </button>
                </div>
              )}
          </div>
        </div>

        {formState.status === LEGAL_RESEARCH_REQUEST_STATUS.completed.value &&
          contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") && (
            <div className="mb-6 flex justify-center">
              <BroadcastCard
                onClick={handleBroadcastToPublic}
                isPublished={formState.is_published}
              />
            </div>
          )}

        {formState.research_completion_letter_file && (
          <>
            <div className="flex mb-4">
              <p className="font-semibold text-base mb-1">
                {t("RegistrarLetterReferenceNumber")}:
              </p>
              <p className="font-semibold text-base mb-1">
                {formState.letter_reference_number}
              </p>
            </div>
            <div className="max-w-4xl bg-white rounded-lg p-6 border border-gray-400 mb-4 h-fit">
              <p className="font-semibold text-base mb-1">{t("MessengerDetail")}</p>
              <p className="text-[#B8B8B8] font-regular text-sm mb-1">
                {formState?.messenger_name}
              </p>
              <div className="flex mt-4">
                <FileView
                  title={t("ResearchCompletionLetterFile")}
                  file={formState?.research_completion_letter_file}
                />
              </div>
            </div>
          </>
        )}
        {!formState.proposal_summary &&
          (formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value) &&
          formState.proposal_deadline && (
            <div className="bg-[#F7F7F7] py-3 px-4 mb-2 border border-gray-400 rounded-lg">
              <div className="flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="my-auto"
                  width="37"
                  height="37"
                  viewBox="0 0 37 37"
                  fill="none"
                >
                  <path
                    d="M18.5007 22.083C18.855 22.083 19.2014 21.9779 19.496 21.7811C19.7907 21.5842 20.0203 21.3044 20.1559 20.977C20.2915 20.6496 20.327 20.2894 20.2579 19.9418C20.1888 19.5943 20.0181 19.275 19.7675 19.0244C19.517 18.7739 19.1977 18.6032 18.8502 18.5341C18.5026 18.465 18.1424 18.5005 17.815 18.6361C17.4876 18.7717 17.2078 19.0013 17.0109 19.2959C16.8141 19.5906 16.709 19.937 16.709 20.2913C16.709 20.7665 16.8977 21.2222 17.2338 21.5582C17.5698 21.8942 18.0255 22.083 18.5007 22.083ZM27.459 22.083C27.8133 22.083 28.1597 21.9779 28.4544 21.7811C28.749 21.5842 28.9787 21.3044 29.1143 20.977C29.2499 20.6496 29.2854 20.2894 29.2162 19.9418C29.1471 19.5943 28.9765 19.275 28.7259 19.0244C28.4753 18.7739 28.1561 18.6032 27.8085 18.5341C27.461 18.465 27.1007 18.5005 26.7733 18.6361C26.446 18.7717 26.1661 19.0013 25.9693 19.2959C25.7724 19.5906 25.6673 19.937 25.6673 20.2913C25.6673 20.7665 25.8561 21.2222 26.1921 21.5582C26.5281 21.8942 26.9838 22.083 27.459 22.083ZM18.5007 29.2497C18.855 29.2497 19.2014 29.1446 19.496 28.9477C19.7907 28.7509 20.0203 28.471 20.1559 28.1436C20.2915 27.8163 20.327 27.456 20.2579 27.1085C20.1888 26.7609 20.0181 26.4417 19.7675 26.1911C19.517 25.9405 19.1977 25.7699 18.8502 25.7008C18.5026 25.6316 18.1424 25.6671 17.815 25.8027C17.4876 25.9383 17.2078 26.168 17.0109 26.4626C16.8141 26.7572 16.709 27.1036 16.709 27.458C16.709 27.9332 16.8977 28.3889 17.2338 28.7249C17.5698 29.0609 18.0255 29.2497 18.5007 29.2497ZM27.459 29.2497C27.8133 29.2497 28.1597 29.1446 28.4544 28.9477C28.749 28.7509 28.9787 28.471 29.1143 28.1436C29.2499 27.8163 29.2854 27.456 29.2162 27.1085C29.1471 26.7609 28.9765 26.4417 28.7259 26.1911C28.4753 25.9405 28.1561 25.7699 27.8085 25.7008C27.461 25.6316 27.1007 25.6671 26.7733 25.8027C26.446 25.9383 26.1661 26.168 25.9693 26.4626C25.7724 26.7572 25.6673 27.1036 25.6673 27.458C25.6673 27.9332 25.8561 28.3889 26.1921 28.7249C26.5281 29.0609 26.9838 29.2497 27.459 29.2497ZM9.54232 22.083C9.89668 22.083 10.2431 21.9779 10.5377 21.7811C10.8324 21.5842 11.062 21.3044 11.1976 20.977C11.3332 20.6496 11.3687 20.2894 11.2996 19.9418C11.2304 19.5943 11.0598 19.275 10.8092 19.0244C10.5586 18.7739 10.2394 18.6032 9.89185 18.5341C9.5443 18.465 9.18406 18.5005 8.85668 18.6361C8.52929 18.7717 8.24947 19.0013 8.0526 19.2959C7.85573 19.5906 7.75065 19.937 7.75065 20.2913C7.75065 20.7665 7.93942 21.2222 8.27542 21.5582C8.61142 21.8942 9.06714 22.083 9.54232 22.083ZM31.0423 4.16634H29.2507V2.37467C29.2507 1.89949 29.0619 1.44378 28.7259 1.10777C28.3899 0.771772 27.9342 0.583008 27.459 0.583008C26.9838 0.583008 26.5281 0.771772 26.1921 1.10777C25.8561 1.44378 25.6673 1.89949 25.6673 2.37467V4.16634H11.334V2.37467C11.334 1.89949 11.1452 1.44378 10.8092 1.10777C10.4732 0.771772 10.0175 0.583008 9.54232 0.583008C9.06714 0.583008 8.61142 0.771772 8.27542 1.10777C7.93942 1.44378 7.75065 1.89949 7.75065 2.37467V4.16634H5.95898C4.53345 4.16634 3.16629 4.73263 2.15829 5.74064C1.15028 6.74865 0.583984 8.1158 0.583984 9.54134V31.0413C0.583984 32.4669 1.15028 33.834 2.15829 34.842C3.16629 35.85 4.53345 36.4163 5.95898 36.4163H31.0423C32.4679 36.4163 33.835 35.85 34.843 34.842C35.851 33.834 36.4173 32.4669 36.4173 31.0413V9.54134C36.4173 8.1158 35.851 6.74865 34.843 5.74064C33.835 4.73263 32.4679 4.16634 31.0423 4.16634ZM32.834 31.0413C32.834 31.5165 32.6452 31.9722 32.3092 32.3082C31.9732 32.6442 31.5175 32.833 31.0423 32.833H5.95898C5.4838 32.833 5.02809 32.6442 4.69208 32.3082C4.35608 31.9722 4.16732 31.5165 4.16732 31.0413V14.9163H32.834V31.0413ZM32.834 11.333H4.16732V9.54134C4.16732 9.06616 4.35608 8.61044 4.69208 8.27444C5.02809 7.93844 5.4838 7.74967 5.95898 7.74967H31.0423C31.5175 7.74967 31.9732 7.93844 32.3092 8.27444C32.6452 8.61044 32.834 9.06616 32.834 9.54134V11.333ZM9.54232 29.2497C9.89668 29.2497 10.2431 29.1446 10.5377 28.9477C10.8324 28.7509 11.062 28.471 11.1976 28.1436C11.3332 27.8163 11.3687 27.456 11.2996 27.1085C11.2304 26.7609 11.0598 26.4417 10.8092 26.1911C10.5586 25.9405 10.2394 25.7699 9.89185 25.7008C9.5443 25.6316 9.18406 25.6671 8.85668 25.8027C8.52929 25.9383 8.24947 26.168 8.0526 26.4626C7.85573 26.7572 7.75065 27.1036 7.75065 27.458C7.75065 27.9332 7.93942 28.3889 8.27542 28.7249C8.61142 29.0609 9.06714 29.2497 9.54232 29.2497Z"
                    fill="black"
                  />
                </svg>
                <div className="ml-4 my-auto">
                  <p className="text-sm text-[#212121] font-semibold">
                    {t("DeadlineForProposal")}
                  </p>
                  {(() => {
                    const days = calcRemainingDays(formState.proposal_deadline);
                    const label =
                      days < 0
                        ? t("OverdueByDays", { count: Math.abs(days) })
                        : days === 0
                          ? t("Today")
                          : t("DaysLeft", { count: days });
                    const cls =
                      days < 0
                        ? "bg-red-100 text-red-700"
                        : days === 0
                          ? "bg-orange-100 text-orange-700"
                          : days <= 3
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-green-100 text-green-700";
                    return (
                      <span
                        className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}
                      >
                        {label}
                      </span>
                    );
                  })()}
                </div>
                <div className="text-base font-normal ml-auto flex my-auto">
                  <p className="font-semibold mr-2">
                    {formatDay(formState.proposal_deadline).weekday},
                  </p>
                  <p>{formatDay(formState.proposal_deadline).rest}</p>
                </div>
              </div>
              {formState?.feedback_from_management_file && (
                <div className="flex mt-4">
                  <FileView
                    title={t("FeedbackFile")}
                    file={formState?.feedback_from_management_file}
                  />
                </div>
              )}
            </div>
          )}

        {/* Add Feedback — coordinator adds proposal feedback; form appears directly below the button */}
        {formState.status ===
          LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value &&
          !formState.proposal_summary &&
          (formState.forward_status ===
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value ||
            formState.forward_status ===
              LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value) &&
          formState.requested_by === "internal" &&
          contextData?.user?.roles?.some(
            (p) => p === "LegalResearchCoordinator"
          ) && (
            <div className="mb-2">
              <div className="flex">
                <button
                  onClick={() => setIsFeedBackFormOpen(!isFeedbackFormOpen)}
                  type="button"
                  className="ml-auto flex items-center gap-1.5 text-sm font-medium text-[#0A1D39] border border-[#0A1D39] rounded-lg px-3 py-1.5 hover:bg-[#0A1D39]/5 transition-colors"
                >
                  {isFeedbackFormOpen ? (
                    t("Cancel")
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      {t("AddFeedBack")}
                    </>
                  )}
                </button>
              </div>
              {isFeedbackFormOpen && (
                <div className="mt-3">
                  <ManagementFeedback
                    setIsFeedBackFormOpen={setIsFeedBackFormOpen}
                    status={LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value}
                    formState={formState}
                    setFormState={setFormState}
                    setIsRespondBackFormOpen={setIsRespondBackFormOpen}
                  />
                </div>
              )}
            </div>
          )}

        {/* Research topic header — Proposal Presentation is shown directly below it */}
        <div className="mb-4 border-l-4 border-[#9B0505] pl-4">
          <p className="text-[11px] uppercase tracking-wider text-[#9B0505] font-semibold mb-0.5">
            {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"} · {t("Topic")}
          </p>
          <h1 className="text-2xl font-bold text-[#0A1D39] leading-snug">
            {topic?.topic_title || "—"}
          </h1>
        </div>

        {schedules &&
          formState.internal_topic_schdule?.find(
            (schedule: any) => schedule.purpose === "proposal_presentation"
          ) && (
            <div className={`py-4 px-6 border-2 rounded-lg shadow-md bg-white ${isUrgent ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${urgencyConfig.bgColor} ${isUrgent ? 'text-red-800' : 'text-gray-800'}`}>
                  {urgencyConfig.label}
                </div>
                <div className="text-xs text-gray-500">
                  {remainingDays} {t("DayUntilPresentation", { count: remainingDays })}
                </div>
              </div>

              <div
                onClick={() => {
                  if (
                    formState?.status ===
                      LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value &&
                    (contextData?.user?.roles?.some(
                      (p) => p === "GeneralAttorney"
                    ) ||
                      contextData?.user?.roles?.some(
                        (p) => p === "OAGBManagementHead"
                      ))
                  ) {
                    setIsScheduleModalOpen(!isScheduleModalOpen);
                  } else if (
                    !(
                      formState?.forward_status ===
                      LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value
                    )
                  ) {
                    setIsFeedBackFormOpen(!isFeedbackFormOpen);
                  } else {
                    setIsScheduleModalOpen(!isScheduleModalOpen);
                  }
                }}
                className="flex cursor-pointer"
              >
                <div className="p-3 bg-gray-100 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 text-gray-700"
                    width="37"
                    height="37"
                    viewBox="0 0 37 37"
                    fill="none"
                  >
                    <path
                      d="M18.5007 22.083C18.855 22.083 19.2014 21.9779 19.496 21.7811C19.7907 21.5842 20.0203 21.3044 20.1559 20.977C20.2915 20.6496 20.327 20.2894 20.2579 19.9418C20.1888 19.5943 20.0181 19.275 19.7675 19.0244C19.517 18.7739 19.1977 18.6032 18.8502 18.5341C18.5026 18.465 18.1424 18.5005 17.815 18.6361C17.4876 18.7717 17.2078 19.0013 17.0109 19.2959C16.8141 19.5906 16.709 19.937 16.709 20.2913C16.709 20.7665 16.8977 21.2222 17.2338 21.5582C17.5698 21.8942 18.0255 22.083 18.5007 22.083ZM27.459 22.083C27.8133 22.083 28.1597 21.9779 28.4544 21.7811C28.749 21.5842 28.9787 21.3044 29.1143 20.977C29.2499 20.6496 29.2854 20.2894 29.2162 19.9418C29.1471 19.5943 28.9765 19.275 28.7259 19.0244C28.4753 18.7739 28.1561 18.6032 27.8085 18.5341C27.461 18.465 27.1007 18.5005 26.7733 18.6361C26.446 18.7717 26.1661 19.0013 25.9693 19.2959C25.7724 19.5906 25.6673 19.937 25.6673 20.2913C25.6673 20.7665 25.8561 21.2222 26.1921 21.5582C26.5281 21.8942 26.9838 22.083 27.459 22.083ZM18.5007 29.2497C18.855 29.2497 19.2014 29.1446 19.496 28.9477C19.7907 28.7509 20.0203 28.471 20.1559 28.1436C20.2915 27.8163 20.327 27.456 20.2579 27.1085C20.1888 26.7609 20.0181 26.4417 19.7675 26.1911C19.517 25.9405 19.1977 25.7699 18.8502 25.7008C18.5026 25.6316 18.1424 25.6671 17.815 25.8027C17.4876 25.9383 17.2078 26.168 17.0109 26.4626C16.8141 26.7572 16.709 27.1036 16.709 27.458C16.709 27.9332 16.8977 28.3889 17.2338 28.7249C17.5698 29.0609 18.0255 29.2497 18.5007 29.2497ZM27.459 29.2497C27.8133 29.2497 28.1597 29.1446 28.4544 28.9477C28.749 28.7509 28.9787 28.471 29.1143 28.1436C29.2499 27.8163 29.2854 27.456 29.2162 27.1085C29.1471 26.7609 28.9765 26.4417 28.7259 26.1911C28.4753 25.9405 28.1561 25.7699 27.8085 25.7008C27.461 25.6316 27.1007 25.6671 26.7733 25.8027C26.446 25.9383 26.1661 26.168 25.9693 26.4626C25.7724 26.7572 25.6673 27.1036 25.6673 27.458C25.6673 27.9332 25.8561 28.3889 26.1921 28.7249C26.5281 29.0609 26.9838 29.2497 27.459 29.2497ZM9.54232 22.083C9.89668 22.083 10.2431 21.9779 10.5377 21.7811C10.8324 21.5842 11.062 21.3044 11.1976 20.977C11.3332 20.6496 11.3687 20.2894 11.2996 19.9418C11.2304 19.5943 11.0598 19.275 10.8092 19.0244C10.5586 18.7739 10.2394 18.6032 9.89185 18.5341C9.5443 18.465 9.18406 18.5005 8.85668 18.6361C8.52929 18.7717 8.24947 19.0013 8.0526 19.2959C7.85573 19.5906 7.75065 19.937 7.75065 20.2913C7.75065 20.7665 7.93942 21.2222 8.27542 21.5582C8.61142 21.8942 9.06714 22.083 9.54232 22.083ZM31.0423 4.16634H29.2507V2.37467C29.2507 1.89949 29.0619 1.44378 28.7259 1.10777C28.3899 0.771772 27.9342 0.583008 27.459 0.583008C26.9838 0.583008 26.5281 0.771772 26.1921 1.10777C25.8561 1.44378 25.6673 1.89949 25.6673 2.37467V4.16634H11.334V2.37467C11.334 1.89949 11.1452 1.44378 10.8092 1.10777C10.4732 0.771772 10.0175 0.583008 9.54232 0.583008C9.06714 0.583008 8.61142 0.771772 8.27542 1.10777C7.93942 1.44378 7.75065 1.89949 7.75065 2.37467V4.16634H5.95898C4.53345 4.16634 3.16629 4.73263 2.15829 5.74064C1.15028 6.74865 0.583984 8.1158 0.583984 9.54134V31.0413C0.583984 32.4669 1.15028 33.834 2.15829 34.842C3.16629 35.85 4.53345 36.4163 5.95898 36.4163H31.0423C32.4679 36.4163 33.835 35.85 34.843 34.842C35.851 33.834 36.4173 32.4669 36.4173 31.0413V9.54134C36.4173 8.1158 35.851 6.74865 34.843 5.74064C33.835 4.73263 32.4679 4.16634 31.0423 4.16634ZM32.834 31.0413C32.834 31.5165 32.6452 31.9722 32.3092 32.3082C31.9732 32.6442 31.5175 32.833 31.0423 32.833H5.95898C5.4838 32.833 5.02809 32.6442 4.69208 32.3082C4.35608 31.9722 4.16732 31.5165 4.16732 31.0413V14.9163H32.834V31.0413ZM32.834 11.333H4.16732V9.54134C4.16732 9.06616 4.35608 8.61044 4.69208 8.27444C5.02809 7.93844 5.4838 7.74967 5.95898 7.74967H31.0423C31.5175 7.74967 31.9732 7.93844 32.3092 8.27444C32.6452 8.61044 32.834 9.06616 32.834 9.54134V11.333ZM9.54232 29.2497C9.89668 29.2497 10.2431 29.1446 10.5377 28.9477C10.8324 28.7509 11.062 28.471 11.1976 28.1436C11.3332 27.8163 11.3687 27.456 11.2996 27.1085C11.2304 26.7609 11.0598 26.4417 10.8092 26.1911C10.5586 25.9405 10.2394 25.7699 9.89185 25.7008C9.5443 25.6316 9.18406 25.6671 8.85668 25.8027C8.52929 25.9383 8.24947 26.168 8.0526 26.4626C7.85573 26.7572 7.75065 27.1036 7.75065 27.458C7.75065 27.9332 7.93942 28.3889 8.27542 28.7249C8.61142 29.0609 9.06714 29.2497 9.54232 29.2497Z"
                      fill="black"
                    />
                  </svg>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-lg font-bold text-gray-900">
                    {t("ProposalPresentation")}
                  </p>
                  {schedules.location && (
                    <p className="text-sm text-gray-600 font-medium">
                      📍 {schedules?.location}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    {t("ScheduledForPresentation")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">
                    {formatDay(schedules.scheduled_datetime).weekday}
                  </p>
                  <p className="text-sm text-gray-600">
                    {formatDay(schedules.scheduled_datetime).rest}
                  </p>
                  {isUrgent && (
                    <p className="text-xs text-red-600 font-semibold mt-1">
                      ⚠️ {t("UrgentAttentionRequired")}
                    </p>
                  )}
                </div>
              </div>
              {(contextData?.user?.roles?.some(
                (p) => p === "LRDIFUDDirector"
              ) ||
                contextData?.user?.roles?.some(
                  (p) => p === "LegalResearchCoordinator"
                )) &&
                schedules?.purpose === "proposal_presentation" &&
                formState.forward_status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator
                    .value &&
                formState.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value && (
                  <ManagementFeedback
                    setIsFeedBackFormOpen={setIsFeedBackFormOpen}
                    status={
                      LEGAL_RESEARCH_REQUEST_STATUS
                        .proposal_presentation_feedback_submitted.value
                    }
                    formState={formState}
                    setFormState={setFormState}
                    setIsRespondBackFormOpen={setIsRespondBackFormOpen}
                  />
                )}
              {formState.feedback_from_management &&
                formState.status !==
                  LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h2 className="text-base text-[#212121] font-semibold">
                      {formState.status ===
                      LEGAL_RESEARCH_REQUEST_STATUS.proposal_rejected.value
                        ? t("ProposalRejectionReason")
                        : formState.status ===
                            LEGAL_RESEARCH_REQUEST_STATUS
                              .proposal_presentation_feedback_submitted.value
                          ? t("ProposalPresentationFeedback") ||
                            "Proposal Presentation Feedback"
                          : t("ProposalFileFeedback")}
                    </h2>
                    <p className="mt-2 text-[#606060]">
                      {formState.feedback_from_management}
                    </p>
                    {formState.documents?.find(
                      (document: any) =>
                        document.document_type === "internal_management_feedback" ||
                        document.document_type ===
                          "feedback_from_management_file",
                    ) && (
                      <DocumentViewerModal
                        documents={formState?.documents.filter(
                          (document: any) =>
                            document.document_type === "internal_management_feedback" ||
                            document.document_type ===
                              "feedback_from_management_file",
                        )}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`}
                      />
                    )}
                  </div>
                )}
            </div>
          )}
        {/* Professional Topic/Title Presentation Card */}
        {formState.internal_topic_schdule?.find(
          (schedule: any) => schedule.purpose === "title_presentation"
        ) && (
          <div className="py-4 px-6 border-2 rounded-lg shadow-md bg-white mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                  {t("TopicPresentations")}
                </span>
              </div>
              <div className="text-sm text-gray-500">
                {t("Scheduled")}
              </div>
            </div>

            <div
              onClick={() => {
                if (
                  formState?.status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value &&
                  contextData?.user?.roles?.some(
                    (p) => p === "GeneralAttorney"
                  )
                ) {
                  setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
                } else if (
                  formState?.status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value &&
                  contextData?.user?.roles?.some(
                    (p) => p === "GeneralAttorney"
                  )
                ) {
                  setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
                } else if (
                  formState?.status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
                  contextData?.user?.roles?.some(
                    (p) => p === "GeneralAttorney"
                  )
                ) {
                  setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
                }
              }}
              className="flex items-center p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
            >
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-8 h-8 text-blue-700"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                  <path d="M8 14h.01"></path>
                  <path d="M12 14h.01"></path>
                  <path d="M16 14h.01"></path>
                  <path d="M8 18h.01"></path>
                  <path d="M12 18h.01"></path>
                  <path d="M16 18h.01"></path>
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-lg font-bold text-gray-900">
                  {formState.requested_by === "internal" ? t("Topics") : t("Title")} {t("Presentation")}
                </p>
                {formState.internal_topic_schdule?.find(
                  (schedule: any) => schedule.purpose === "title_presentation"
                )?.location && (
                  <p className="text-sm text-gray-600 font-medium">
                    📍 {formState.internal_topic_schdule?.find(
                      (schedule: any) => schedule.purpose === "title_presentation"
                    )?.location}
                  </p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  {t("ClickToEditView", { action: formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value ? t("Edit") : t("View") })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-gray-900">
                  {formatDay(formState.internal_topic_schdule?.find(
                    (schedule: any) => schedule.purpose === "title_presentation"
                  )?.scheduled_datetime).weekday}
                </p>
                <p className="text-sm text-gray-600">
                  {formatDay(formState.internal_topic_schdule?.find(
                    (schedule: any) => schedule.purpose === "title_presentation"
                  )?.scheduled_datetime).rest}
                </p>
              </div>
            </div>

            {(contextData?.user?.roles?.some(
              (p) => p === "GeneralAttorney"
            ) && (
              formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value ||
              formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value ||
              formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value
            )) && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
                  }}
                  className="bg-[#212121] hover:bg-[#333333] text-white text-sm py-2 px-4 rounded-lg transition-colors duration-200 font-medium"
                >
                  {formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value
                    ? t("EditPresentation")
                    : t("ViewEditDetails")}
                </button>
              </div>
            )}
          </div>
        )}
        <div className="flex-1 mt-4">
          {formState?.implementation_direction && (
            <div className="flex w-full gap-8">
              <div className="flex flex-col flex-1 max-w-4xl bg-white rounded-lg shadow-md p-6 mb-4">
                <p className="font-semibold text-base mb-1">
                  {t("ImplementationDirection")}
                </p>
                <p className="text-[#B8B8B8] font-regular text-sm mb-1">
                  {formatDate(formState.updated_at)}
                </p>
                <p className="text-[#606060] font-regular text-base mb-1">
                  {formState?.implementation_direction}
                </p>
              </div>
            </div>
          )}
          {(formState?.proposal_summary ||
            formState?.documents?.find(
              (document: any) => (typeof document.document_type === "string" && document.document_type.includes("proposal"))
            )) && (
            <div className="border border-gray-400 p-4 mb-8 rounded-lg">
              {
                <>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-[#0A1D39]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <h3 className="font-semibold text-base text-[#0A1D39]">
                          {t("ProposalFileSummary")}
                        </h3>
                      </div>
                      {((contextData?.user?.roles?.some(
                        (p) => p === "LegalResearchCoordinator"
                      ) ||
                        contextData?.user?.roles?.some(
                          (p) => p === "LRDIFUDDirector"
                        )) &&
                        (formState?.status === "proposal_requested" ||
                          formState?.status ===
                            LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value)) ||
                        (contextData?.user?.roles?.some(
                          (p) => p === "LRDIFUDDirector"
                        ) &&
                          formState?.forward_status ===
                            "forwarded_to_director") ? (
                          <button
                            onClick={() => {
                              setEditedProposalSummary(
                                formState.proposal_summary || ""
                              );
                              setIsProposalSummaryEditing(
                                !isProposalSummaryEditing
                              );
                            }}
                            className="text-[#0A1D39] hover:bg-[#0A1D39]/5 text-sm font-semibold px-3 py-1 rounded-md transition-colors"
                          >
                            {isProposalSummaryEditing
                              ? t("CancelEdit")
                              : t("EditSummary")}
                          </button>
                        ) : null}
                    </div>
                    {isProposalSummaryEditing ? (
                      <div className="mb-4">
                        <textarea
                          className="w-full p-3 border border-gray-300 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0A1D39] focus:border-transparent resize-y min-h-[100px]"
                          placeholder={t("EnterProposalSummaryPlaceholder")}
                          value={editedProposalSummary}
                          onChange={(e) =>
                            setEditedProposalSummary(e.target.value)
                          }
                        />
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("ReUploadProposalFile") || "Re-upload Proposal File"}
                          </label>
                          <FileUpload onChange={(file: any) => setEditedProposalFile(file)} />
                        </div>
                        <button
                          onClick={handleUpdateProposalSummary}
                          disabled={loading}
                          className="mt-3 bg-[#0A1D39] hover:bg-[#13294d] text-white text-base py-2 px-4 rounded-lg disabled:opacity-60"
                        >
                          {loading ? t("Saving") : t("SaveSummary")}
                        </button>
                      </div>
                    ) : (
                      <p className="mt-2 mb-2 text-[#606060] whitespace-pre-wrap leading-relaxed">
                        {formState.proposal_summary}
                      </p>
                    )}
                    <div className="px-5 mt-4">
                      {formState?.documents?.filter(
                        (document: any) => (typeof document.document_type === "string" && document.document_type.includes("proposal"))
                      ).length > 0 && (
                        <>
                          <div className="mb-4">
                            <h4 className="font-semibold text-sm mb-2">{t("LatestProposalFile")}</h4>
                            <DocumentViewerModal
                              documents={[formState?.documents
                                ?.filter((document: any) => (typeof document.document_type === "string" && document.document_type.includes("proposal")))
                                .sort((a: any, b: any) => b.id - a.id)[0]]}
                              baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`}
                            />
                          </div>
                        </>
                      )}
                      {(formState?.documents?.filter(
                        (document: any) => (typeof document.document_type === "string" && document.document_type.includes("proposal"))
                      ).length > 1 || formState?.file_histories?.some(
                        (fileHistroy: any) => fileHistroy.file_type === "proposal"
                      )) && (
                        <details className="mt-4">
                          <summary className="cursor-pointer font-semibold text-base mb-2">
                            {t("PreviousProposalFiles")}
                          </summary>
                          <div className="border border-gray-300 p-5 mx-5 rounded-lg space-y-4">
                            {formState?.documents
                              ?.filter((document: any) => (typeof document.document_type === "string" && document.document_type.includes("proposal")))
                              .sort((a: any, b: any) => b.id - a.id)
                              .slice(1)
                              .map((document: any) => (
                                <div key={document.id} className="flex items-center justify-between">
                                  <DocumentViewerModal
                                    documents={[document]}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`}
                                  />
                                </div>
                              ))}
                            {formState?.file_histories
                              ?.slice()
                              .reverse()
                              .map(
                                (fileHistroy: any) =>
                                  fileHistroy.file_type === "proposal" && (
                                    <div className="flex items-center justify-between">
                                      <FileView
                                        title={t("Proposal")}
                                        file={fileHistroy?.file}
                                        fileName={
                                          fileHistroy?.file &&
                                          fileHistroy?.file.substring(
                                            fileHistroy?.file.lastIndexOf("/") + 1
                                          )
                                        }
                                      />
                                    </div>
                                  )
                              )}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                </>
              }
              <div className="flex items-center justify-between">
                {(contextData?.user?.roles?.some(
                  (p) => p === "LegalResearchCoordinator"
                ) ||
                  contextData?.user?.roles?.some(
                    (p) => p === "LRDIFUDDirector"
                  )) &&
                  (formState?.status === "proposal_requested" ||
                    formState?.status ===
                      LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value) && (
                    <div className="flex flex-wrap items-center justify-end gap-3 ml-auto mt-4 pt-4 border-t border-gray-200 w-full">
                      <button
                        onClick={handleProposalRespondBackClick}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium text-base py-2 px-8 rounded-lg transition-colors"
                      >
                        {t("RespondBack")}
                      </button>
                      <button
                        onClick={handleProposalApprove}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white font-medium text-base py-2 px-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-60"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t("Approving")}
                          </>
                        ) : contextData?.user?.roles?.some(
                            (p) => p === "LegalResearchCoordinator"
                          ) ? (
                          t("ApproveAndForwardToDirector")
                        ) : (
                          t("ApproveAndForwardToHead")
                        )}
                      </button>
                    </div>
                  )}
                {contextData?.user?.roles?.some(
                  (p) => p === "LRDIFUDDirector"
                ) &&
                  formState?.forward_status === "forwarded_to_director" && (
                    <div className="flex flex-wrap items-center justify-end gap-3 ml-auto mt-4 pt-4 border-t border-gray-200 w-full">
                      <button
                        onClick={handleProposalRespondBackClick}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium text-base py-2 px-8 rounded-lg transition-colors"
                      >
                        {t("RespondBack")}
                      </button>
                      <button
                        onClick={handleProposalApprove}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white font-medium text-base py-2 px-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-60"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t("Approving")}
                          </>
                        ) : contextData?.user?.roles?.some(
                            (p) => p === "LegalResearchCoordinator"
                          ) ? (
                          t("ApproveAndForwardToDirector")
                        ) : (
                          t("ApproveAndForwardToHead")
                        )}
                      </button>
                    </div>
                  )}
                {formState?.status === "proposal_approved" &&
                  formState?.forward_status ===
                    LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
                  (contextData?.user?.roles?.some(
                    (p) => p === "GeneralAttorney",
                  ) ||
                    contextData?.user?.roles?.some(
                      (p) => p === "OAGBManagementHead",
                    )) &&
                  !formState.internal_topic_schdule?.find(
                    (schedule: any) =>
                      schedule.purpose === "proposal_presentation"
                  ) && (
                    <button
                      onClick={() => {
                        setIsScheduleModalOpen(!isScheduleModalOpen);
                      }}
                      className="bg-[#212121] text-white text-base px-8 py-2 px-6 rounded-lg ml-auto"
                    >
                      {t("ScheduleProposalPresentation")}
                    </button>
                  )}
              </div>
            </div>
          )}
          {/* Topic Section */}
          {formState.research_topic && (
            <div className="mb-2 border border-gray-300 p-3 rounded-lg">
              <h3 className="font-semibold text-lg mb-4">{t("ResearchTopic")}</h3>
              <div>
                <div className="flex">
                  <p className="font-semibold text-base mb-2">
                    {t("Title")}: {formState.research_topic}
                  </p>
                  {contextData?.user?.roles?.some(
                    (p) => p === "LegalResearchProsecutor"
                  ) &&
                    formState?.internal_topic_assignment.find(
                      (prosecutor: any) =>
                        prosecutor.prosecutor_user_id ===
                        contextData?.user?.user_id
                    ).is_team_leader &&
                    formState.forward_status ===
                      LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor
                        .value &&
                    (formState.status ===
                      LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value ||
                      formState.status ===
                        LEGAL_RESEARCH_REQUEST_STATUS.title_revision.value) && (
                      <svg
                        onClick={() =>
                          navigate(
                            `/conduct-legal-research/editInternalrequest/${formState.id}`,
                            { state: { formState: formState } }
                          )
                        }
                        className="ml-auto mb-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer"
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="black"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                          fill="black"
                        />
                      </svg>
                    )}
                </div>
                {formState.research_topic_abstract.split('\n').map((para: string, index: number) => (
                  <p key={index} className="text-[#606060] font-normal text-base leading-relaxed mb-2">
                    {para}
                  </p>
                ))}
              </div>
              <br />
              {formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value && (
                <div>
                  <p className="font-semibold text-base mb-2">
                    {t("TitleApprovalRemark")}
                  </p>
                  <p className="text-[#606060] font-normal text-base leading-relaxed">
                    {formState.title_remark}
                  </p>
                </div>
              )}
              {/* Documents */}
              <div className="mt-2">
                {formState?.research_synopsis_file && (
                  <>
                    <h4 className="font-semibold text-base mb-2">{t("ResearchSynopsis")}</h4>
                    <div className="flex items-center justify-between p-3">
                      <FileView
                        title={t("ResearchSynopsis")}
                        file={formState?.research_synopsis_file}
                        fileName={
                          formState?.research_synopsis_file &&
                          formState?.research_synopsis_file.substring(
                            formState?.research_synopsis_file.lastIndexOf("/") + 1
                          )
                        }
                      />
                      {formState?.file_histories?.find(
                        (fileHistroy: any) => fileHistroy.file_type === "synopsis"
                      ) && (
                        <svg
                          onClick={() =>
                            setShowSynopsisHistory(!showSynopsisHistory)
                          }
                          className="my-auto ml-4 h-8"
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="8"
                          viewBox="0 0 10 8"
                          fill="none"
                        >
                          <path
                            d="M5 8L0.669872 0.499999L9.33013 0.5L5 8Z"
                            fill="#D9D9D9"
                          />
                        </svg>
                      )}
                    </div>
                    {showSynopsisHistory && (
                      <div className="border border-gray-300 p-5 mx-5 rounded-lg space-y-4">
                        {formState?.file_histories
                          ?.slice()
                          .reverse()
                          .map(
                            (fileHistroy: any) =>
                              fileHistroy.file_type === "synopsis" && (
                                <div className="flex items-center justify-between">
                                  <FileView
                                    title={t("ResearchSynopsis")}
                                    file={fileHistroy?.file}
                                    fileName={
                                      fileHistroy?.file &&
                                      fileHistroy?.file.substring(
                                        fileHistroy?.file.lastIndexOf("/") + 1
                                      )
                                    }
                                  />
                                </div>
                              )
                          )}
                      </div>
                    )}
                  </>
                )}

                {formState?.letter_file && (
                  <>
                    <h4 className="font-semibold text-base mb-2 mt-4">{t("Letter")}</h4>
                    <div className="flex items-center justify-between p-3 rounded-lg">
                      <FileView
                        title={t("Letter")}
                        file={formState?.letter_file}
                        fileName={
                          formState?.letter_file &&
                          formState?.letter_file.substring(
                            formState?.letter_file.lastIndexOf("/") + 1
                          )
                        }
                      />
                    </div>
                  </>
                )}
              </div>
              <br />
              {(formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.title_requested.value ||
                (formState.status ===
                  LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
                  edit)) &&
                contextData?.user?.roles?.some(
                  (p) => p === "LegalResearchCoordinator"
                ) && (
                  <div>
                    <div className="flex flex-col">
                      <label className="mb-4">{t("Remark")}</label>
                      <textarea
                        value={remark}
                        onChange={handleRemarkChange}
                        className="px-3 py-2 bg-[#F2F2F2] outline-none rounded-sm h-24"
                      />
                    </div>
                    <div className="flex gap-4 mt-4 text-white">
                      <button
                        onClick={() => {
                          handleProposalRespondBackClick();
                          setRespondBackStatus(
                            LEGAL_RESEARCH_REQUEST_STATUS.title_revision.value
                          );
                        }}
                        className="bg-gray-300 flex-1 text-black text-base py-2 px-8 rounded-lg ml-6"
                      >
                        {t("RespondBack")}
                      </button>
                      <button
                        onClick={handleApproveTitle}
                        className="hover:bg-[#141414] flex-1 bg-[#212121] rounded-lg p-2 flex items-center justify-center"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t("Approving")}
                          </>
                        ) : (
                          t("ApproveTitle")
                        )}
                      </button>
                    </div>
                  </div>
                )}
            </div>
          )}
          {(contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") ||
            contextData?.user?.roles?.some(
              (p) => p === "LegalResearchCoordinator"
            )) &&
          formState?.internal_topic_assignment?.find(
            (prosecutor: any) =>
              prosecutor.team_type === "prosecutor_research_team"
          ) &&
          !formState.proposal_summary &&
          ((!formState.title_presentation_feedback &&
            !(
              formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_rejected.value
            ) &&
            formState.forward_status ===
              LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
            (contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") ||
              contextData?.user?.roles?.some(
                (p) => p === "LegalResearchCoordinator"
              )) &&
            schedules?.purpose === "title_presentation") ||
            (isFeedbackFormOpen)) ? (
            <>
              <br />
              <ManagementFeedback
                setIsFeedBackFormOpen={setIsFeedBackFormOpen}
                status={LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value}
                formState={formState}
                setFormState={setFormState}
                setIsRespondBackFormOpen={setIsRespondBackFormOpen}
              />
            </>
          ) : (
            formState.title_presentation_feedback && (
              <div className="mt-4 mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Official header */}
                <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                      {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                    </p>
                    <h3 className="text-white font-bold text-base truncate">
                      {t("TitleRemarkFromManagement")}
                    </h3>
                  </div>
                </div>
                {/* Body */}
                <div className="p-5">
                  <p className="text-[#3a3a3a] text-sm leading-relaxed whitespace-pre-line">
                    {formState.title_presentation_feedback}
                  </p>
                  {formState?.documents?.find(
                    (document: any) =>
                      document.document_type === "internal_management_feedback" ||
                      document.document_type === "feedback_from_management_file",
                  ) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <DocumentViewerModal
                        documents={formState?.documents.filter(
                          (document: any) =>
                            document.document_type === "internal_management_feedback" ||
                            document.document_type ===
                              "feedback_from_management_file",
                        )}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`}
                      />
                    </div>
                  )}
                </div>
              </div>
            )
          )}
          {formState?.documents?.find(
            (document: any) =>
              document.document_type === "feedback_from_management_file"
          ) && (
            <div className="mb-8 border border-gray-300 p-4 rounded-lg">
              <p className="text-[#432101AB] font-medium mb-2">
                {t("RemarkFromManagement")}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {formState.feedback_from_management}
              </p>
              {formState?.documents?.find(
                (document: any) =>
                  document.document_type === "feedback_from_management_file"
              ) && (
                <DocumentViewerModal
                  documents={formState?.documents.filter(
                    (document: any) =>
                      document.document_type === "feedback_from_management_file"
                  )}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/internal-legal-research-documents/document`}
                />
              )}
            </div>
          )}
          {formState?.remark_for_director && (
            <div className="mb-8">
              <p className="text-[#432101AB] font-medium mb-2">
                {t("RemarkOfHeadOfOAGB")}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {formState.remark_for_director}
              </p>
            </div>
          )}
          {formState?.remark_for_coordinator && (
            <div className="mb-8">
              <p className="text-[#432101AB] font-medium mb-2">
                {t("RemarkOfDirector")}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {formState.remark_for_coordinator}
              </p>
            </div>
          )}
          {formState?.remark_for_team && (
            <div className="mb-8 p-3 rounded-lg border border-gray-400">
              <p className="text-[#432101AB] font-medium mb-2">
                {t("RemarkOfCoordinator")}
              </p>
              <p className="text-gray-700 leading-relaxed">
                {formState.remark_for_team}
              </p>
            </div>
          )}
          {/* Team (under Research Topic) */}
          {formState?.internal_topic_assignment &&
            formState?.internal_topic_assignment.find(
              (prosecutor: any) =>
                prosecutor.team_type === "prosecutor_research_team"
            ) && (
              <div className="mt-6 mb-6 flex bg-gray-50 rounded-lg border border-gray-200">
                <ActionAvatars
                  title={t("TeamList")}
                  users={formState?.internal_topic_assignment.filter(
                    (prosecutor: any) =>
                      prosecutor.team_type === "prosecutor_research_team"
                  )}
                  prosecutor={true}
                />
                {(contextData?.user?.roles?.some(
                  (p) => p === "LegalResearchCoordinator"
                ) ||
                  contextData?.user?.roles?.some(
                    (p) => p === "LRDIFUDDirector"
                  )) && (
                    <svg
                      onClick={() => setIsTeamModalOpen(true)}
                      className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer"
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                        fill="black"
                      />
                    </svg>
                  )}
                <svg
                  onClick={() => setViewTeam(!viewTeam)}
                  className="mr-2 ml-auto my-auto cursor-pointer"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <mask
                    id="mask0_9_31087"
                    maskUnits="userSpaceOnUse"
                    x="1"
                    y="5"
                    width="22"
                    height="14"
                  >
                    <path
                      d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                      fill="white"
                      stroke="white"
                      stroke-width="1.33333"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                      fill="black"
                      stroke="black"
                      stroke-width="1.33333"
                      stroke-linejoin="round"
                    />
                  </mask>
                  <g mask="url(#mask0_9_31087)">
                    <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                  </g>
                </svg>
              </div>
            )}
          <br />
        </div>
        {formState?.status ===
          LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value &&
          !schedules &&
          contextData?.user?.roles?.some((p) => p === "GeneralAttorney") && (
            <button
              onClick={() => {
                setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
              }}
              className="bg-[#212121] hover:bg-[#333333] text-white text-sm py-3 px-6 rounded-lg ml-6 transition-colors duration-200 font-medium shadow-sm"
            >
              {t("ScheduleTopicsPresentation", { type: formState.requested_by === "internal" ? "Topics" : "Title" })}
            </button>
          )}
        {formState?.status ===
          LEGAL_RESEARCH_REQUEST_STATUS.title_accepted.value &&
          !schedules &&
          contextData?.user?.roles?.some((p) => p === "GeneralAttorney") && (
            <button
              onClick={() => {
                setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
              }}
              className="bg-[#212121] hover:bg-[#333333] text-white text-sm py-3 px-6 rounded-lg ml-6 transition-colors duration-200 font-medium shadow-sm"
            >
              {t("ScheduleTopicPresentation")}
            </button>
          )}
        {(formState?.status ===
          LEGAL_RESEARCH_REQUEST_STATUS.title_presentation.value ||
          formState?.status ===
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_management.value) &&
          contextData?.user?.roles?.some((p) => p === "GeneralAttorney") && (
            <button
              onClick={() => {
                setIsScheduleTitleModalOpen(!isScheduleTitleModalOpen);
              }}
              className="bg-[#212121] hover:bg-[#333333] text-white text-sm py-3 px-6 rounded-lg ml-6 transition-colors duration-200 font-medium shadow-sm"
            >
              {t("EditTopicPresentation")}
            </button>
          )}
        {/* Upload Proposal Button */}
        {contextData?.user?.roles?.some(
          (p) => p === "LegalResearchProsecutor"
        ) &&
          formState?.forward_status ===
            LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value &&
          (formState?.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_requested.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_revision.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value ||
            formState.status ===
              LEGAL_RESEARCH_REQUEST_STATUS.proposal_update.value) &&
          formState?.internal_topic_assignment.find(
            (prosecutor: any) =>
              prosecutor.prosecutor_user_id === contextData?.user?.user_id
          ).is_team_leader && (
            <button
              className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md w-full"
              onClick={handleUploadProposalClick}
            >
              <div className="flex justify-center">
                {formState?.proposal_summary && <>{t("Re")}</>}
                <p>{t("UploadProposal")}</p>
              </div>
            </button>
          )}
        {/* Assign Coordinator Button */}
        {contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
          formState.forward_status ===
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value &&
          formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleAssignCoordinatorClick}
                className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md flex-1"
              >
                <div className="flex justify-center">
                  {formState.remark_for_coordinator && <p>{t("Re")}</p>}
                  <p>{t("AssignCoordinator")}</p>
                </div>
              </button>
              <button
                onClick={handleAssignProsecutorClick}
                className="bg-white border border-gray-800 text-gray-800 hover:bg-gray-100 font-bold py-3 px-6 rounded-md flex-1"
              >
                <div className="flex justify-center">
                  {formState.remark_for_team && <p>{t("Re")}</p>}
                  <p>{t("AssignProsecutors")}</p>
                </div>
              </button>
            </div>
          )}

        {formState.forward_status ===
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
          formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value &&
          (!formState.remark_for_director || edit) && (
            <form onSubmit={handleRespondBack}>
              <>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                  <label className="block text-sm font-medium text-gray-500 min-w-[120px]">
                    {t("SetLevelOfUrgency")}
                  </label>
                  <br />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={getUrgencyButtonClasses("High")}
                      onClick={() => handleUrgencyClick("High")}
                    >
                      {t("High")}
                    </button>
                    <button
                      type="button"
                      className={getUrgencyButtonClasses("Medium")}
                      onClick={() => handleUrgencyClick("Medium")}
                    >
                      {t("Medium")}
                    </button>
                    <button
                      type="button"
                      className={getUrgencyButtonClasses("Low")}
                      onClick={() => handleUrgencyClick("Low")}
                    >
                      {t("Low")}
                    </button>
                  </div>
                </div>
                <div className="mt-6">
                  <label
                    htmlFor="remark"
                    className="block text-sm font-medium text-gray-500"
                  >
                    {t("Remark")}
                  </label>
                  <textarea
                    id="remark"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-24 resize-y"
                    placeholder={t("DetailHere")}
                    required
                    value={remark}
                    onChange={handleRemarkChange}
                  ></textarea>
                </div>
                <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                  <button
                    type="submit"
                    className="ml-auto px-6 py-3 rounded-md bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        {LoadingIcon} {t("Submitting")}
                      </div>
                    ) : (
                      t("RespondBack")
                    )}
                  </button>
                  <div
                    onClick={handleSubmit}
                    className="cursor-pointer px-6 py-3 rounded-md bg-gray-800 text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        {LoadingIcon} {t("Submitting")}
                      </div>
                    ) : (
                      t("Submit")
                    )}
                  </div>
                </div>
              </>
            </form>
          )}
        {/* Assign Coordinator Button */}
        {contextData?.user?.roles?.some(
          (p) => p === "LegalResearchCoordinator"
        ) && (
          <div className="text-center">
            {formState.forward_status ===
              LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value &&
              formState.status ===
                LEGAL_RESEARCH_REQUEST_STATUS.title_approved.value && (
                <button
                  onClick={handleAssignProsecutorClick}
                  className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md w-full"
                >
                  <div className="flex justify-center">
                    {formState.remark_for_team && <p>{t("Re")}</p>}
                    <p>{t("AssignProsecutors")}</p>
                  </div>
                </button>
              )}
          </div>
        )}
        {formState.remark_for_director &&
          formState.forward_status ===
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value &&
          formState.status === LEGAL_RESEARCH_REQUEST_STATUS.pending.value && (
            <button
              onClick={() =>
                handleForward(
                  LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value
                )
              }
              type="button"
              className="bg-[#212121] text-white rounded-lg p-2 px-8 mt-2"
            >
              {t("ForwardToDirector")}
            </button>
          )}
      </div>
      {viewTeam && (
        <TeamView
          teamMembers={formState.internal_topic_assignment.filter(
            (prosecutor: any) =>
              prosecutor.team_type === "prosecutor_research_team"
          )}
          onClose={() => setViewTeam(false)}
        />
      )}
      {(contextData?.user?.roles?.some(
        (p) => p === "LegalResearchCoordinator"
      ) ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) && (
        <SettingUpTeamModal
          request={formState}
          setLegalResearch={setFormState}
          isOpen={isTeamModalOpen}
          onClose={handleCloseTeamModal}
          onAssignProsecutor={handleAssignProsecutorClick}
        />
      )}
      {(contextData?.user?.roles?.some(
        (p) => p === "LegalResearchCoordinator"
      ) ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) && (
        <RespondBackForm
          request={formState}
          setLegalResearch={setFormState}
          isOpen={isRespondBackFormOpen}
          onClose={() => setIsRespondBackFormOpen(false)}
          forward={respondBackForward}
          status={respondBackStatus}
        />
      )}
      {contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") &&
        formState?.internal_topic_assignment?.find(
          (prosecutor: any) =>
            prosecutor.prosecutor_user_id === contextData?.user?.user_id
        ).is_team_leader && (
          <UploadProposal
            request={formState}
            setLegalResearch={setFormState}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onUploadProposal={handleUploadProposalClick}
          />
        )}
      {(contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
        contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector")) &&
        (formState?.status === "proposal_requested" ||
          formState?.status ===
            LEGAL_RESEARCH_REQUEST_STATUS.proposal_updated.value) && (
          <UploadProposal
            request={formState}
            setLegalResearch={setFormState}
            isOpen={isEditProposalModalOpen}
            onClose={() => setIsEditProposalModalOpen(false)}
            onUploadProposal={() => {}}
          />
        )}
      {contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") && (
        <AssignCoordinatorModal
          data={formState}
          setLegalResearch={setFormState}
          isOpen={isAssignModalOpen}
          onClose={handleAssignCloseModal}
        />
      )}
      {contextData?.user?.roles?.some((p) => p === "GeneralAttorney") && (
        <ScheduleTitlePresentationModal
          data={formState}
          setLegalResearch={setFormState}
          isOpen={isScheduleTitleModalOpen}
          onClose={handleSchduleTitleCloseModal}
        />
      )}
      {(contextData?.user?.roles?.some((p) => p === "GeneralAttorney") ||
        contextData?.user?.roles?.some((p) => p === "OAGBManagementHead")) &&
        formState.status ===
          LEGAL_RESEARCH_REQUEST_STATUS.proposal_approved.value && (
          <SchedulePresentationModal
            data={formState}
            setLegalResearch={setFormState}
            isOpen={isScheduleModalOpen}
            onClose={handleSchduleCloseModal}
          />
        )}
    </>
  );
}

export default InternalProposalDocument;